import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { useAppForm } from "@/components/ui/forms/hooks/useFormHook"
import { cn } from "@/modules/lib/utils"
import {
  mediaBlockFormSchema,
  type MediaBlockFormikType,
} from "@/modules/sessions/sessionForm/blocks/mediaBlock/mediaBlockFormikTypes"
import { useSidebar } from "@/modules/sidebar/hooks/useSidebar"
import { useConfirmPopup } from "@/modules/ui/components/popup/huConfirmPopup"
import { word } from "@modules/core/utils/i18n"
import { MediaUpload } from "@modules/ui/components/fileUpload/mediaUpload"
import { LoaderCircle, XIcon } from "lucide-react"
import { useCallback, useEffect, useRef } from "react"

type Props = {
  updatedMediaBlock?: MediaBlockFormikType
  updatedMediaBlockIndex: number
  onValidate: (mediaBlock: MediaBlockFormikType) => Promise<void>
  className?: string
} & React.ComponentProps<"div">

export function MediaForm({ updatedMediaBlock, updatedMediaBlockIndex, onValidate, className, ...props }: Props) {
  const onCloseSidebarFnRef = useRef<() => void>(() => undefined)
  const onCloseMediaFormFnRef = useRef<() => Promise<boolean>>(() => Promise.resolve(true))
  const { updateSidebarStackOnTop, hideSidebarStackOnTop, hideSidebar } = useSidebar()
  const confirmPopup = useConfirmPopup()

  const defaultMediaBlock: MediaBlockFormikType = updatedMediaBlock ?? { medias: [] }

  const form = useAppForm({
    defaultValues: defaultMediaBlock,
    validators: {
      onSubmit: mediaBlockFormSchema,
      onChange: mediaBlockFormSchema,
    },
    onSubmit: async ({ value }) => {
      await onValidate(value)
    },
  })

  const onCloseMediaForm = useCallback(
    async (shouldHideSidebar: boolean) => {
      // Close the sidebar top stack and hide the sidebar if the shouldHideSidebar is true
      const closeStack = async () => {
        hideSidebarStackOnTop()
        if (shouldHideSidebar) {
          // Wait for the sidebar top stack to be hidden before hiding the sidebar
          await new Promise((resolve) => setTimeout(resolve, 50))
          hideSidebar()
        }
      }

      // If there is no updated note, close the sidebar top stack and return false
      if (!updatedMediaBlock) {
        await closeStack()
        return Promise.resolve(false)
      } else {
        // If there is an updated note and the form is dirty, show the confirm popup
        if (form.state.isDirty) {
          const confirm = await new Promise<boolean>((resolve) => {
            confirmPopup.show({
              title: word("program.media.closeMessage.title"),
              message: word("program.media.closeMessage.message"),
              accept: async () => {
                await closeStack()
                resolve(false)
              },
              cancel: () => undefined,
              footerProps: {
                confirmText: word("global.ok"),
              },
            })
          })
          return confirm
        } else {
          // If there is an updated note and the form is not dirty, close the sidebar top stack and return false
          await closeStack()
          return Promise.resolve(false)
        }
      }
    },
    [updatedMediaBlock, confirmPopup, hideSidebarStackOnTop, hideSidebar, form],
  )

  // Defines the custom header buttons for the sidebar top stack
  const customHeaderButtons = [
    <Button
      key="close-media-form"
      variant="outline"
      size="iconMd"
      onClick={() => {
        onCloseSidebarFnRef.current()
      }}
    >
      <XIcon color="currentColor" size={16} />
    </Button>,
  ]

  useEffect(() => {
    // Add the custom header buttons and the left button click handler to the sidebar top stack
    updateSidebarStackOnTop({ customHeaderButtons, sidebarLeftButtonClick: () => onCloseMediaFormFnRef.current() })
  }, [])

  useEffect(() => {
    // Set the onCloseSidebarFnRef and onCloseMediaFormFnRef to the onCloseMediaForm function
    onCloseSidebarFnRef.current = () => {
      void onCloseMediaForm(true)
    }
    onCloseMediaFormFnRef.current = () => {
      return onCloseMediaForm(false)
    }
  }, [onCloseMediaForm])

  const handleSubmit = async () => {
    console.log(form.state.errors)
    // Validate every field with the "submit" cause so errors are surfaced (fields get marked as touched)
    await form.validateAllFields("submit")
    if (!form.state.isValid) return
    await form.handleSubmit()
  }

  return (
    <div className={cn("flex flex-1 flex-col justify-between gap-4 px-5 pt-18 pb-6", className)} {...props}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Text fontStyle="LS" className="text-textDefault-body uppercase">
            {`(${String(updatedMediaBlockIndex + 1)}) ${word("program.planning.create.exercise.block_plural", { count: updatedMediaBlockIndex })}`}
          </Text>

          <Text fontStyle="TL">{word("sessionForm.blocks.add_media.title")}</Text>
          <Text fontStyle="BS">{word("sessionForm.blocks.add_media.description")}</Text>
        </div>

        <form.AppField name="medias">
          {(field) => (
            <field.Field>
              <MediaUpload
                type="mediaBlockItem"
                mediaList={field.state.value}
                updateMediaList={(mediaList) => {
                  field.handleChange(mediaList)
                }}
                updateMediaItem={(mediaItem, index) => {
                  const newMediaList = [...field.state.value]
                  newMediaList[index] = mediaItem
                  field.handleChange(newMediaList)
                }}
              />
              <field.Message />
            </field.Field>
          )}
        </form.AppField>
      </div>

      <div className="flex flex-col items-start justify-between">
        <form.Subscribe
          selector={(state) => [state.isSubmitting, state.values.medias.some((media) => media.isUploading)] as const}
        >
          {([isSubmitting, isUploadingAMedia]) => (
            <Button
              size="lg"
              variant="primary"
              className="w-full"
              onClick={() => void handleSubmit()}
              disabled={isUploadingAMedia || isSubmitting}
            >
              {isSubmitting && <LoaderCircle className="animate-spin" />}
              {updatedMediaBlock ? word("global.save") : word("global.check")}
            </Button>
          )}
        </form.Subscribe>
      </div>
    </div>
  )
}
